// GS1 DataBar barcode parser.
//
// Cannabis suppliers ship product with GS1-encoded barcodes that combine
// the product GTIN, lot/batch number, expiry, and other fields into a
// single scan string. Scanning one of these emits a string like
// `010062824280438710WGS13200418||17500723` — that's not a single 38-char
// barcode, it's a sequence of `(AI)(value)` segments that need to be
// pulled apart before any product lookup can succeed.
//
// This file is a TypeScript port of waio-portal's `parse_gs1_barcode`
// (`snippets/barcodes.py:34-149`). Behavior must match V1: same patterns
// in the same order, same handling of pipe separators, same "leading
// zeros stripped" GTIN canonicalization. The doctest cases from V1 live
// in `gs1.test.ts` so anyone tweaking the parser can verify parity.
//
// Reference: https://www.barcodefaq.com/barcode-properties/definitions/gs1-application-identifiers/

/** Fields extracted from a GS1 scan. Only `gtin` is guaranteed; the rest
 *  reflect whichever AIs the scan contained. */
export interface Gs1ParseResult {
  /** GTIN with leading zeros stripped (the value to look up by). */
  gtin: string
  /** Lot / batch number (AI 10). */
  lot_number?: string
  /** Expiration date YYMMDD (AI 17). */
  exp_date?: string
  /** Production date YYMMDD (AI 11). */
  prod_date?: string
  /** Pack date YYMMDD (AI 13). */
  pack_date?: string
  /** Best-before date YYMMDD (AI 15). */
  best_before_date?: string
  /** Sell-by date YYMMDD (AI 16). */
  sell_by_date?: string
  /** Serial number (AI 21). */
  serial_number?: string
}

// AI patterns, tried in order. Each entry is [field-name, regex-anchored-at-^].
// The regex captures the value; the full match length tells us how many
// chars to consume from the input.
//
// Variable-length fields (AI 10, 21) use `[^|]{1,20}` — they consume up
// to 20 non-pipe chars and stop at either a pipe separator or end-of-
// string. The trailing `\|*` swallows any pipes between fields. This
// matches V1's `(\|+)?` (zero-or-one group of pipes) effectively the
// same way; `\|*` is "zero or more" which is a strict superset.
//
// **Order assumption.** Variable-length AIs (10, 21) are greedy: they
// match up to 20 non-pipe chars regardless of whether a later AI prefix
// appears mid-value. So a scan like
//   `010062824280438710WGS1320041817500723`
// (order: 01, 10, 17 with NO pipe between lot and exp) parses as
// lot_number='WGS1320041817500723' and silently drops exp_date. V1 has
// the same limitation and no V1 doctest exercises this ordering — real
// cannabis suppliers either put fixed-length AIs (dates) BEFORE the lot
// or use `|` separators. Proper FNC1 (ASCII 29) handling would fix this
// generically; revisit if a supplier ever ships scans in 10-then-17
// order without separators.
//
// Field names use the underscore-style ('lot_number', 'exp_date') from
// V1 rather than camelCase, so the JSDoc examples can be diff'd against
// the Python doctests without renaming.
//
// Internal `f-XX` placeholders are AIs we don't expose but still need to
// recognize so the parser can skip past them and reach later fields.
type Pattern = readonly [field: string, re: RegExp]

const PATTERNS: Pattern[] = [
  ['sscc-18', /^00(\d{18})/],
  ['gtin-14', /^01(\d{14})/],
  ['gtin-14-c', /^02(\d{14})/],
  ['f-03', /^03(\d{6})/],
  ['f-04', /^04(\d{6})/],
  ['lot_number', /^10([^|]{1,20})\|*/],
  ['prod_date', /^11(\d{6})/],
  ['due_date', /^12(\d{6})/],
  ['pack_date', /^13(\d{6})/],
  ['f-14', /^14(\d{6})/],
  ['best_before_date', /^15(\d{6})/],
  ['sell_by_date', /^16(\d{6})/],
  ['exp_date', /^17(\d{6})/],
  ['f-18', /^18(\d{6})/],
  ['f-19', /^19(\d{6})/],
  ['variant', /^20(\d{2})/],
  ['serial_number', /^21([^|]{1,20})\|*/],
]

/** Parse a scanned string as GS1. Returns `null` if the input doesn't
 *  contain a recognizable GTIN — callers should treat that as "this is
 *  a non-GS1 scan, look it up as a raw barcode."
 *
 *  The function never throws; malformed GS1 fragments degrade to
 *  whatever-was-extracted-up-to-that-point, returning what was parsed
 *  so long as a GTIN was found.
 *
 *  Examples (from V1 doctests):
 *
 *    parseGs1Barcode('0100628582000654104042282055238577||17500723')
 *      → { gtin: '628582000654', lot_number: '4042282055238577', exp_date: '500723' }
 *
 *    parseGs1Barcode('010062824280438710WGS13200418')
 *      → { gtin: '628242804387', lot_number: 'WGS13200418' }
 *
 *    parseGs1Barcode('0100628242804387')
 *      → { gtin: '628242804387' }
 *
 *    parseGs1Barcode('049000028911')   // plain UPC, not GS1
 *      → null
 */
export function parseGs1Barcode(input: string): Gs1ParseResult | null {
  if (!input) return null

  // V1 strips literal parens before parsing — some scanners are
  // configured to emit `(01)006282…(10)WGS…` with the AI markers
  // wrapped. Strip them so the patterns line up either way. Also strip
  // surrounding pipes (V1: `barcode.strip('|')`) but leave internal
  // pipes alone — those are field separators that the variable-length
  // AI patterns consume on their own.
  const cleaned = input.replace(/[()]/g, '').replace(/^\|+|\|+$/g, '')
  if (!cleaned) return null

  const out: Record<string, string> = {}
  let s = cleaned

  while (s.length > 0) {
    let matched = false
    for (const [field, re] of PATTERNS) {
      const m = re.exec(s)
      if (!m) continue
      // First-match-wins on duplicate fields. Well-formed GS1 doesn't
      // repeat AIs, but if it does we keep the earlier value.
      if (!(field in out)) out[field] = m[1]
      s = s.slice(m[0].length)
      matched = true
      break
    }
    // Unknown AI prefix → stop. Whatever we've parsed so far stands;
    // we don't try to skip ahead and resume.
    if (!matched) break
  }

  // The canonical product code is GTIN-14, falling back to SSCC-18 or
  // GTIN-14-C. If none of these were present, this isn't a GS1
  // product code — bail and let the caller treat it as a raw barcode.
  const raw = out['gtin-14'] ?? out['sscc-18'] ?? out['gtin-14-c']
  if (!raw) return null

  // V1: `b.lstrip('0')`. Strips ALL leading zeros — `'00628242804387'`
  // becomes `'628242804387'` (the UPC-A form).
  //
  // V1 returns `('', payload)` for an all-zero GTIN; we return null
  // instead so the caller treats it as "not a useful GS1 scan" and
  // routes to raw-barcode lookup with the original input. Sending an
  // empty `code` to /products/by-barcode would either 400 or silently
  // misroute, neither of which is what we want.
  const gtin = raw.replace(/^0+/, '')
  if (!gtin) return null

  const result: Gs1ParseResult = { gtin }
  if (out['lot_number']) result.lot_number = out['lot_number']
  if (out['exp_date']) result.exp_date = out['exp_date']
  if (out['prod_date']) result.prod_date = out['prod_date']
  if (out['pack_date']) result.pack_date = out['pack_date']
  if (out['best_before_date']) result.best_before_date = out['best_before_date']
  if (out['sell_by_date']) result.sell_by_date = out['sell_by_date']
  if (out['serial_number']) result.serial_number = out['serial_number']
  return result
}
